/* eslint-disable react/prop-types -- this codebase does not use propTypes (see
   CLAUDE.md); the local presentational components below would otherwise each
   report every prop they take. */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  updateProfile,
  updatePassword,
  signOut,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { auth } from "../lib/firebase";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchUser } from "../api/queries";
import { updateUser } from "../api/mutations";
import { getFirebaseErrorMessage } from "../utils/firebaseErrorHandler";
import { safeStorage, safeSessionStorage } from "../utils/storage";
import {
  passwordStrength,
  MIN_PASSWORD_LENGTH,
} from "../utils/passwordStrength";
import Header from "../components/Header";
import Button from "../components/Button";
import Avatar from "../components/Avatar";
import {
  UserCircleIcon,
  KeyIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardIcon,
  CheckCircleIcon,
  ArrowRightOnRectangleIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

const PROVIDER_LABEL_KEYS = {
  password: "profile.providers.email",
  "google.com": "profile.providers.google",
};

const STRENGTH_BAR_COLORS = {
  weak: "bg-red-600",
  fair: "bg-yellow-600",
  strong: "bg-green-600",
};

function Card({ children, className = "" }) {
  return (
    <div className={`bg-white shadow-sm rounded-lg ${className}`}>
      {children}
    </div>
  );
}

function CardBody({ children, className = "" }) {
  return <div className={`px-4 py-6 sm:px-8 ${className}`}>{children}</div>;
}

function CardFooter({ children }) {
  return (
    <div className="flex items-center justify-end gap-x-3 border-t border-gray-900/10 px-4 py-4 sm:px-8">
      {children}
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-x-2">
      {Icon && <Icon className="h-5 w-5 text-gray-500" />}
      <h2 className="text-base font-semibold leading-7 text-gray-900">
        {children}
      </h2>
    </div>
  );
}

// Feedback belongs to the card that produced it. The old page kept one `success`
// and one `error` for the whole page and rendered them in the first form only,
// so "password updated" appeared above the name field.
function Feedback({ feedback }) {
  if (!feedback) return null;

  const isError = feedback.type === "error";

  return (
    <div
      role="status"
      className={`mb-6 rounded-md p-4 text-sm ${
        isError ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"
      }`}
    >
      {feedback.message}
    </div>
  );
}

function PasswordField({ id, label, value, onChange, error, children }) {
  const [revealed, setRevealed] = useState(false);
  const { t } = useLanguage();

  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium leading-6 text-gray-900"
      >
        {label}
      </label>
      {/* Ring outside the box and children clipped, as on the API key field:
          an inset ring paints under the children, so any background the reveal
          button draws would eat the border along its edges. */}
      <div
        className={`mt-2 flex items-center overflow-hidden rounded-md bg-white shadow-xs ring-1 ${
          error ? "ring-red-300" : "ring-gray-300"
        } focus-within:ring-2 focus-within:ring-indigo-600`}
      >
        <input
          type={revealed ? "text" : "password"}
          id={id}
          value={value}
          onChange={onChange}
          className="block w-full flex-1 rounded-md border-0 bg-transparent py-1.5 pl-3 text-gray-900 focus:ring-0 sm:text-sm sm:leading-6"
        />
        {/* Icon only: the eye is universal here, and it keeps the control the
            same width in all nine languages. The label still has to exist for
            screen readers and as a tooltip — hence aria-label and title. */}
        <button
          type="button"
          onClick={() => setRevealed((shown) => !shown)}
          aria-pressed={revealed}
          aria-label={
            revealed
              ? t("profile.changePassword.hide")
              : t("profile.changePassword.show")
          }
          title={
            revealed
              ? t("profile.changePassword.hide")
              : t("profile.changePassword.show")
          }
          className="flex items-center self-stretch px-3 text-gray-500 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:bg-gray-100"
        >
          {revealed ? (
            <EyeSlashIcon className="h-4 w-4" />
          ) : (
            <EyeIcon className="h-4 w-4" />
          )}
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {children}
    </div>
  );
}

function Requirement({ met, children }) {
  return (
    <div
      className={`mt-2.5 flex items-center gap-x-2 text-sm ${
        met ? "text-green-700" : "text-gray-500"
      }`}
    >
      {met ? (
        <CheckIcon className="h-4 w-4" />
      ) : (
        <span className="h-4 w-4 rounded-full border-2 border-gray-300" />
      )}
      {children}
    </div>
  );
}

function StrengthMeter({ password }) {
  const { t } = useLanguage();
  const { level, filled } = passwordStrength(password);

  if (level === "none") return null;

  return (
    <div className="mt-3 flex items-center gap-x-3">
      <div className="flex flex-1 gap-x-1">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className={`h-1 flex-1 rounded-full ${
              index < filled ? STRENGTH_BAR_COLORS[level] : "bg-gray-200"
            }`}
          />
        ))}
      </div>
      <span className="text-xs font-medium text-gray-600">
        {t(`profile.changePassword.strength.${level}`)}
      </span>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6" aria-hidden="true">
      <Card>
        <div className="flex items-center gap-x-5 px-4 py-6 sm:px-8">
          <div className="h-16 w-16 animate-pulse rounded-full bg-gray-200" />
          <div className="flex-auto space-y-2.5">
            <div className="h-4 w-44 animate-pulse rounded bg-gray-200" />
            <div className="h-3.5 w-60 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      </Card>
      {[0, 1].map((index) => (
        <Card key={index}>
          <CardBody>
            <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
            <div className="mt-6 h-9 w-full max-w-md animate-pulse rounded-md bg-gray-200" />
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [fullName, setFullName] = useState(user?.displayName || "");
  const [profileFeedback, setProfileFeedback] = useState(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState(null);
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [apiKeyRevealed, setApiKeyRevealed] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const copyTimeout = useRef(null);

  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: ["user"],
    queryFn: fetchUser,
    enabled: !!user,
  });

  const updateUserMutation = useMutation({ mutationFn: updateUser });

  useEffect(() => {
    return () => clearTimeout(copyTimeout.current);
  }, []);

  // Firebase records how this account signs in. A Google account has no password
  // credential, so reauthenticateWithCredential would always fail for it — hide
  // the card rather than offer a form that cannot succeed. An unknown provider
  // keeps the old behaviour (form shown).
  const providerId = user?.providerData?.[0]?.providerId;
  const isPasswordAccount = !providerId || providerId === "password";
  const providerLabelKey = PROVIDER_LABEL_KEYS[providerId];

  const displayName = userData?.name || user?.displayName || "";
  const email = userData?.email || user?.email || "";
  const avatarUrl = userData?.avatar || user?.photoURL;

  const meetsLength = newPassword.length >= MIN_PASSWORD_LENGTH;
  const passwordsMatch =
    confirmPassword.length > 0 && newPassword === confirmPassword;
  const canSubmitPassword =
    currentPassword.length > 0 && meetsLength && passwordsMatch;

  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordErrors({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handleConfirmNewPasswordChange = (e) => {
    const updatedConfirmPassword = e.target.value;
    setConfirmPassword(updatedConfirmPassword);

    // Clear password error if password becomes valid
    if (
      passwordErrors.confirmPassword &&
      updatedConfirmPassword === newPassword
    ) {
      setPasswordErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    // A blank name is not rejected by the API: User#set_default_name rewrites it
    // to the email local-part, so the save would "succeed" and quietly replace
    // the name shown in the team list and invitations. The disabled Save button
    // is what stops it in the UI (a disabled default button also suppresses
    // Enter-key implicit submission); this is the backstop for a direct submit.
    const trimmedName = fullName.trim();
    if (!trimmedName) return;
    setProfileFeedback(null);
    setIsSavingProfile(true);

    try {
      // The name lives in two places: Firebase (displayName, what the top bar
      // reads) and the Rails users table (what invitations and the team list
      // read). Updating only Firebase left the API copy stale.
      await updateProfile(auth.currentUser, { displayName: trimmedName });
      await updateUserMutation.mutateAsync({ data: { name: trimmedName } });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      setProfileFeedback({
        type: "success",
        message: t("profile.success.profile"),
      });
    } catch (error) {
      // The two awaits above fail differently. updateProfile rejects with a
      // Firebase error; mutateAsync rejects with an axios one, whose code
      // ("ERR_BAD_RESPONSE", …) is not in FIREBASE_ERROR_KEYS. Sending it to
      // getFirebaseErrorMessage reported every ordinary API failure to Bugsnag
      // as an auth error and printed the raw English "Request failed with
      // status code N". The interceptor in api/axios.js already toasts the
      // backend's own message, so here we only say the save did not stick —
      // which matters, because Firebase accepted the name a line earlier and
      // the two copies have now diverged.
      const isFirebaseError =
        typeof error?.code === "string" && error.code.startsWith("auth/");

      setProfileFeedback({
        type: "error",
        message: isFirebaseError
          ? getFirebaseErrorMessage(error, t)
          : t("profile.errors.saveFailed"),
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setPasswordFeedback(null);
    setPasswordErrors({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    if (newPassword !== confirmPassword) {
      setPasswordErrors((prev) => ({
        ...prev,
        confirmPassword: t("profile.errors.passwordMismatch"),
      }));
      return;
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordErrors((prev) => ({
        ...prev,
        newPassword: t("profile.errors.passwordLength"),
      }));
      return;
    }

    setIsSavingPassword(true);

    try {
      // Re-authenticate user before password change
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword,
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Update password
      await updatePassword(auth.currentUser, newPassword);

      resetPasswordForm();
      setIsEditingPassword(false);
      setPasswordFeedback({
        type: "success",
        message: t("profile.success.password"),
      });
    } catch (error) {
      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        setPasswordErrors((prev) => ({
          ...prev,
          currentPassword: t("auth.firebaseErrors.wrongPassword"),
        }));
      } else if (error.code === "auth/too-many-requests") {
        setPasswordErrors((prev) => ({
          ...prev,
          currentPassword: getFirebaseErrorMessage(error, t),
        }));
      } else if (error.code === "auth/weak-password") {
        setPasswordErrors((prev) => ({
          ...prev,
          newPassword: getFirebaseErrorMessage(error, t),
        }));
      } else {
        setPasswordFeedback({
          type: "error",
          message: getFirebaseErrorMessage(error, t),
        });
      }
    } finally {
      setIsSavingPassword(false);
    }
  };

  // Escape hatch for the signed-in user who cannot produce their current
  // password: the change form is unusable without it, and the /reset-password
  // page is only reachable from the logged-out screens.
  const handleSendResetEmail = async () => {
    setPasswordFeedback(null);
    setIsSendingReset(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
    } catch (error) {
      setPasswordFeedback({
        type: "error",
        message: getFirebaseErrorMessage(error, t),
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(userData?.api_key || "");
    setApiKeyCopied(true);
    clearTimeout(copyTimeout.current);
    copyTimeout.current = setTimeout(() => setApiKeyCopied(false), 2000);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      safeStorage.clear();
      safeSessionStorage.clear();
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  if (isUserLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      <Header
        icon={UserCircleIcon}
        title={t("profile.title")}
        description={t("profile.description")}
      />

      {/* Identity */}
      <Card>
        <div className="flex items-center gap-x-5 px-4 py-6 sm:px-8">
          <Avatar
            src={avatarUrl}
            name={displayName || email}
            className="h-16 w-16 text-xl"
          />
          <div className="min-w-0 flex-auto">
            <p className="truncate text-base font-semibold text-gray-900">
              {displayName}
            </p>
            <p className="mt-1 truncate text-sm text-gray-500">{email}</p>
          </div>
          {providerLabelKey && (
            <span className="hidden shrink-0 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200 sm:inline-flex">
              {t(providerLabelKey)}
            </span>
          )}
        </div>
      </Card>

      {/* Personal information */}
      <Card>
        <form onSubmit={handleUpdateProfile}>
          <CardBody>
            <SectionTitle>{t("profile.personalInfo.title")}</SectionTitle>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              {t("profile.personalInfo.description")}
            </p>

            <div className="mt-6">
              <Feedback feedback={profileFeedback} />
              <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    {t("profile.form.fullName")}
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      id="fullName"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    {t("profile.form.email")}
                  </label>
                  <div className="mt-2 flex items-center rounded-md bg-gray-50 shadow-xs ring-1 ring-inset ring-gray-300">
                    <input
                      type="email"
                      id="email"
                      value={email}
                      readOnly
                      className="block w-full flex-1 rounded-md border-0 bg-transparent py-1.5 pl-3 text-gray-600 focus:ring-0 sm:text-sm sm:leading-6"
                    />
                    <LockClosedIcon className="mr-3 h-4 w-4 shrink-0 text-gray-400" />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    {t("profile.form.emailHint")}
                  </p>
                </div>
              </div>
            </div>
          </CardBody>
          <CardFooter>
            <Button
              type="submit"
              isSaving={isSavingProfile}
              disabled={!fullName.trim()}
            >
              {t("profile.form.save")}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {/* Change password — collapsed until the user asks for it */}
      {isPasswordAccount && (
        <Card>
          {isEditingPassword ? (
            <form onSubmit={handlePasswordReset}>
              <CardBody>
                <SectionTitle>{t("profile.changePassword.title")}</SectionTitle>
                <p className="mt-1 text-sm leading-6 text-gray-500">
                  {t("profile.changePassword.description")}
                </p>

                {/* The collapsed branch below renders this too, but only the
                    success path closes the form — every error leaves it open, so
                    without a copy here auth/requires-recent-login,
                    auth/network-request-failed, auth/internal-error and every
                    handleSendResetEmail failure set feedback nothing displays. */}
                <div className="mt-6 max-w-md">
                  <Feedback feedback={passwordFeedback} />
                </div>

                <div className="max-w-md space-y-5">
                  <PasswordField
                    id="currentPassword"
                    label={t("profile.changePassword.currentPassword")}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    error={passwordErrors.currentPassword}
                  >
                    {resetEmailSent ? (
                      <p className="mt-2 text-sm text-green-700">
                        {t("profile.changePassword.resetSent", { email })}
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSendResetEmail}
                        disabled={isSendingReset}
                        className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
                      >
                        {isSendingReset
                          ? t("auth.resetPassword.sending")
                          : t("profile.changePassword.forgot")}
                      </button>
                    )}
                  </PasswordField>

                  <PasswordField
                    id="newPassword"
                    label={t("profile.changePassword.newPassword")}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    error={passwordErrors.newPassword}
                  >
                    <StrengthMeter password={newPassword} />
                    <Requirement met={meetsLength}>
                      {t("profile.changePassword.minLength", {
                        count: MIN_PASSWORD_LENGTH,
                      })}
                    </Requirement>
                  </PasswordField>

                  <PasswordField
                    id="confirmPassword"
                    label={t("profile.changePassword.confirmPassword")}
                    value={confirmPassword}
                    onChange={handleConfirmNewPasswordChange}
                    error={passwordErrors.confirmPassword}
                  >
                    {passwordsMatch && (
                      <Requirement met>
                        {t("profile.changePassword.match")}
                      </Requirement>
                    )}
                  </PasswordField>
                </div>
              </CardBody>
              <CardFooter>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    // The banner belongs to the attempt being abandoned. Left
                    // set, it follows the card into its collapsed state, where
                    // it also suppresses the •••• row below it. Cleared here
                    // rather than in resetPasswordForm, which the success path
                    // calls just before setting its own feedback — mirrors the
                    // Change button, which already clears inline.
                    setPasswordFeedback(null);
                    resetPasswordForm();
                    setIsEditingPassword(false);
                  }}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmitPassword}
                  isSaving={isSavingPassword}
                >
                  {t("profile.changePassword.button")}
                </Button>
              </CardFooter>
            </form>
          ) : (
            <CardBody className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <SectionTitle>{t("profile.changePassword.title")}</SectionTitle>
                <p className="mt-1 text-sm leading-6 text-gray-500">
                  {t("profile.changePassword.description")}
                </p>
                <Feedback feedback={passwordFeedback} />
                {!passwordFeedback && (
                  <div className="mt-3 flex items-center gap-x-2.5 text-sm tracking-widest text-gray-500">
                    <LockClosedIcon className="h-5 w-5 shrink-0 text-gray-400" />
                    ••••••••••••
                  </div>
                )}
              </div>
              <div className="mt-4 sm:ml-6 sm:mt-0 sm:shrink-0">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setPasswordFeedback(null);
                    setIsEditingPassword(true);
                  }}
                >
                  {t("profile.changePassword.change")}
                </Button>
              </div>
            </CardBody>
          )}
        </Card>
      )}

      {/* API Key */}
      {userData?.api_key && (
        <Card>
          <CardBody>
            <SectionTitle icon={KeyIcon}>
              {t("profile.apiKey.title")}
            </SectionTitle>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              {t("profile.apiKey.description")}
            </p>

            <div className="mt-6 max-w-xl">
              <label
                htmlFor="apiKey"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {t("profile.apiKey.label")}
              </label>
              {/* Ring drawn OUTSIDE the box, not inset: an inset ring is painted
                  under the children, so the buttons' hover fill wiped out the
                  border along their edges and the control looked broken. The
                  overflow-hidden clips those fills to the rounded corners. */}
              <div className="mt-2 flex overflow-hidden rounded-md shadow-xs ring-1 ring-gray-300 focus-within:ring-2 focus-within:ring-indigo-600">
                <input
                  type={apiKeyRevealed ? "text" : "password"}
                  id="apiKey"
                  value={userData?.api_key || ""}
                  readOnly
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-3 font-mono text-gray-900 focus:ring-0 sm:text-sm sm:leading-6"
                />

                <button
                  type="button"
                  onClick={() => setApiKeyRevealed((shown) => !shown)}
                  aria-pressed={apiKeyRevealed}
                  aria-label={
                    apiKeyRevealed
                      ? t("profile.apiKey.hide")
                      : t("profile.apiKey.reveal")
                  }
                  title={
                    apiKeyRevealed
                      ? t("profile.apiKey.hide")
                      : t("profile.apiKey.reveal")
                  }
                  className="flex items-center border-l border-gray-200 px-3 py-2 text-gray-500 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:bg-gray-100"
                >
                  {apiKeyRevealed ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleCopyApiKey}
                  aria-label={t("profile.apiKey.copy")}
                  title={
                    apiKeyCopied
                      ? t("profile.apiKey.copied")
                      : t("profile.apiKey.copy")
                  }
                  className={`flex items-center border-l border-gray-200 px-3 py-2 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-100 ${
                    apiKeyCopied
                      ? "text-green-700"
                      : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  {apiKeyCopied ? (
                    <CheckCircleIcon className="h-5 w-5" />
                  ) : (
                    <ClipboardIcon className="h-5 w-5" />
                  )}
                </button>
                {/* The label is gone, so the copy confirmation has to reach a
                    screen reader some other way. */}
                <span role="status" aria-live="polite" className="sr-only">
                  {apiKeyCopied ? t("profile.apiKey.copied") : ""}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Sign out */}
      <Card>
        <CardBody className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <SectionTitle>{t("profile.signOut.title")}</SectionTitle>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              {t("profile.signOut.description")}
            </p>
          </div>
          <div className="mt-4 sm:ml-6 sm:mt-0 sm:shrink-0">
            <Button
              type="button"
              variant="secondary"
              icon={ArrowRightOnRectangleIcon}
              onClick={handleSignOut}
            >
              {t("nav.signOut")}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
